import React, { useState } from "react";
import { Upload, FileCheck2, AlertCircle, X } from "lucide-react";
import { api } from "../../lib/api";
import { apiErrorMessage } from "../../lib/inventoryApi";

const MAX_BYTES = 25 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/jpg", "image/pjpeg", "image/png", "image/webp", "application/pdf"];

/**
 * Reusable file uploader for payment proofs.
 * Calls POST /api/payments/upload-proof-file (additive endpoint).
 *
 * Props:
 *   - paymentRecordId?: string  optional, scopes the object key folder
 *   - onUploaded(payload): callback with { file_url, object_key, size_bytes,
 *                                          mime_type, filename, signed_url }
 *   - disabled?: boolean
 *   - testIdPrefix?: string     for data-testid attributes
 */
// ⛔ `endpoint` DITAMBAHKAN 5 September 2026 — komponen ini menyalakan tiga
//    hal sekaligus (pilih berkas, batasi MIME/ukuran, unggah + tampilkan
//    hasil) dan alamat unggahnya dulu DITANAM. Akibatnya bukti REFUND nol
//    bisa memakainya, lalu ia disimpan sebagai URL yang diketik — teks apa
//    pun, dan NOL PERNAH masuk `file_metadata` sehingga nol pernah ikut
//    backup, di modul yang menyebut berkas itu bukti saat sengketa.
//
// ⚠ Alamat refund SENGAJA endpoint TERSENDIRI, bukan endpoint pembayaran
//   yang dilonggarkan: penjaga `/payments/upload-proof-file` cuma menerima
//   OWNER/KC/CS, sementara yang menutup refund adalah FINANCE. Melonggarkannya
//   berarti memberi Finance hak mengunggah bukti bayar CUSTOMER — wewenang
//   yang keputusan Owner sendiri pisahkan.
//
// ⚠ Bawaannya alamat lama, jadi keempat pemakai yang sudah ada nol berubah.
export default function ProofUploader({ paymentRecordId, onUploaded, disabled = false,
                                        endpoint = "/payments/upload-proof-file",
                                        testIdPrefix = "proof-upload" }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);

  function pick(e) {
    setErr(null);
    setResult(null);
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    if (!ALLOWED.includes(f.type)) {
      setErr("Jenis berkas ini tidak diterima. Hanya JPG/PNG/WEBP/PDF.");
      setFile(null);
      e.target.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      setErr(`Berkas ${(f.size / 1024 / 1024).toFixed(2)} MB melebihi batas 25 MB.`);
      setFile(null);
      e.target.value = "";
      return;
    }
    if (f.size === 0) {
      setErr("Berkas kosong.");
      setFile(null);
      e.target.value = "";
      return;
    }
    setFile(f);
  }

  async function doUpload() {
    if (!file) return;
    setBusy(true);
    setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (paymentRecordId) fd.append("payment_record_id", paymentRecordId);
      const r = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      onUploaded?.(r.data);
    } catch (e) {
      setErr(apiErrorMessage(e, "Bukti gagal diunggah. Coba lagi."));
    } finally {
      setBusy(false);
    }
  }

  function clear() {
    setFile(null);
    setErr(null);
    setResult(null);
  }

  return (
    <div className="border border-dashed border-border rounded-md p-4 bg-muted/40" data-testid={`${testIdPrefix}-root`}>
      <div className="flex items-center gap-2 mb-2">
        <Upload size={16} className="text-muted-foreground" />
        <span className="text-sm font-medium">Unggah bukti pembayaran (maks 25 MB, JPG/PNG/WEBP/PDF)</span>
      </div>
      {!result && (
        <div className="space-y-2">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            disabled={disabled || busy}
            onChange={pick}
            className="block w-full text-sm text-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-border file:bg-background file:text-sm file:font-medium file:cursor-pointer hover:file:bg-muted"
            data-testid={`${testIdPrefix}-input`}
          />
          {file && (
            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground bg-background border border-border rounded px-2 py-1.5">
              <span className="truncate" data-testid={`${testIdPrefix}-filename`}>{file.name} · {(file.size / 1024).toFixed(1)} KB</span>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={doUpload}
                  disabled={busy}
                  className="text-xs bg-primary text-primary-foreground px-3 py-1 rounded hover:bg-primary/90 disabled:opacity-50"
                  data-testid={`${testIdPrefix}-submit`}
                >
                  {busy ? "Mengunggah..." : "Unggah"}
                </button>
                <button type="button" onClick={clear} className="text-muted-foreground hover:text-foreground">
                  <X size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {result && (
        <div className="flex items-start gap-2 bg-success-soft border border-success-soft-border rounded px-3 py-2" data-testid={`${testIdPrefix}-result`}>
          <FileCheck2 size={16} className="text-success mt-0.5" />
          <div className="text-xs text-success-soft-foreground min-w-0 flex-1">
            <div className="font-medium truncate">Terunggah: {result.filename}</div>
            <div className="text-success-soft-foreground">
              {(result.size_bytes / 1024).toFixed(1)} KB
            </div>
            {result.signed_url && (
              <a href={result.signed_url} target="_blank" rel="noopener noreferrer" className="underline" data-testid={`${testIdPrefix}-link`}>
                Buka berkas
              </a>
            )}
          </div>
          <button type="button" onClick={clear} className="text-success hover:text-success-soft-foreground">
            <X size={14} />
          </button>
        </div>
      )}
      {result?.peringatan_bukti_kembar && (
        /* T4: berkas yang sama persis sudah dipakai pembayaran lain — peringatan sejak diunggah, tidak memblokir. */
        <div className="flex items-start gap-2 mt-2 text-xs font-medium text-danger-soft-foreground bg-danger-soft border border-danger-soft-border rounded px-2 py-1.5"
             data-testid={`${testIdPrefix}-kembar`}>
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>{result.peringatan_bukti_kembar}</span>
        </div>
      )}
      {err && (
        <div className="flex items-start gap-2 mt-2 text-xs text-danger-soft-foreground bg-danger-soft border border-danger-soft-border rounded px-2 py-1.5" data-testid={`${testIdPrefix}-error`}>
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span className="break-all">{err}</span>
        </div>
      )}
    </div>
  );
}
